<template>
    <div class="flexibility-level-edit-modal">
        <CoreTwoColumnsRow class="mb-2">
            <template #left>
                <CoreBorderWrapper
                    class="flex-grow-1 flex-shrink-1 flex-basis-45 flex space-between"
                    :condensed="true"
                >
                    <div class="flex-1">
                        {{ $t('§contracts.flexibility_level') }}
                    </div>
                    <CoreIcon
                        v-if="isFlexibilityChangeForbidden"
                        name="information-outline"
                        size="18"
                        class="ml-auto"
                        fillColor="var(--color-secondary-200)"
                        v-tooltip="{ value: $t('§contracts.flexibility_change_forbidden') }"
                    />
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreDropdown
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    v-model="selectedValue"
                    :options="flexibilityLevelOptions"
                    :sort="false"
                    :condensed="true"
                    :disabled="isFlexibilityDisabled"
                />
            </template>
        </CoreTwoColumnsRow>

        <div class="modal-actions">
            <CoreButton
                label="§ui_button.cancel"
                variant="tertiary"
                icon="pi-times"
                :disabled="saving"
                @click="closeModal"
            />
            <CoreButton
                label="§ui_button.save"
                variant="primary"
                icon="pi-save"
                :disabled="!isValueChanged || saving"
                :loading="saving"
                @click="saveFlexibilityLevel"
            />
        </div>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal'
import { useContractStore } from '@/stores/contract'
import ContractService from '@/api/ContractService'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreIcon from '@/components/core/CoreIcon.vue'

export default {
    name: 'FlexibilityLevelEditModal',

    components: {
        CoreTwoColumnsRow,
        CoreBorderWrapper,
        CoreDropdown,
        CoreButton,
        CoreIcon,
    },

    data() {
        return {
            selectedValue: null,
            saving: false,
        }
    },

    created() {
        this.modalStore = useModalStore()
        this.contractStore = useContractStore()
        this.selectedValue = this.modalStore.data.currentValue
    },

    computed: {
        flexibilityLevelOptions() {
            return [
                { label: this.$t('§contracts.flexibility_low'), value: 0.25 },
                { label: this.$t('§contracts.flexibility_medium'), value: 0.5 },
                { label: this.$t('§contracts.flexibility_high'), value: 0.75 },
            ]
        },

        isFlexibilityChangeForbidden() {
            return this.modalStore.data.isChangeForbidden
        },

        isFlexibilityDisabled() {
            return this.modalStore.data.isApproved || this.isFlexibilityChangeForbidden
        },

        isValueChanged() {
            return this.selectedValue !== this.modalStore.data.currentValue
        },
    },

    methods: {
        closeModal() {
            this.modalStore.setClose()
        },

        async saveFlexibilityLevel() {
            if (!this.isValueChanged) return

            this.saving = true

            try {
                await ContractService.updateFlexibilityLevel(
                    { contract: this.modalStore.data.contractId },
                    { split_ppu: this.selectedValue }
                )

                this.contractStore.notification('success', '§ui_message.update_successful')

                await this.contractStore.getContractData({ contract: this.modalStore.data.contractId })

                this.closeModal()
            } catch (error) {
                console.error('Error updating flexibility level:', error)
                this.contractStore.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.flexibility-level-edit-modal {
    padding: 40px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
}
</style>
